/**
 * Client HTTP de ferramentas da IA-API.
 * Base: `/api/v1/ferramentas` — ex. pessoa-cpf (Edge buscar-pessoa-cpf, sem PASI).
 * Auth: Bearer JWT do usuário Supabase.
 */
import { IA_API_FERRAMENTAS_BASE } from './iaApiBase'
import { iaApiFetch, unwrapIaApiData } from './iaApiClient'
import type { IaApiPessoaCpf } from '../types/supportCenterAgenda'

export type { IaApiPessoaCpf } from '../types/supportCenterAgenda'

function mapPessoa(raw: unknown): IaApiPessoaCpf {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { name: null, tax_id_number: null, captured_birth_date: null }
  }
  const row = raw as Record<string, unknown>
  const birth =
    row.captured_birth_date ?? row.birth_date ?? row.data_nascimento ?? row.nascimento ?? null
  return {
    name: typeof row.name === 'string' ? row.name.trim() || null : null,
    tax_id_number:
      typeof row.tax_id_number === 'string'
        ? row.tax_id_number
        : typeof row.cpf === 'string'
          ? row.cpf
          : null,
    captured_birth_date: typeof birth === 'string' ? birth.slice(0, 10) || null : null,
  }
}

async function ferramentasFetch<T>(
  path: string,
  init: RequestInit & { method?: string } = {}
): Promise<T> {
  return iaApiFetch<T>(IA_API_FERRAMENTAS_BASE, path, init, {
    apiLabel: 'consulta de cadastro',
  })
}

export const ferramentasApi = {
  /**
   * GET /ferramentas/pessoa-cpf?cpf= — Edge buscar-pessoa-cpf (sem PASI).
   * Pessoa ausente → HTTP 424 (`IaApiError`, mensagem "Pessoa não encontrada…").
   */
  async getPessoaCpf(cpf: string): Promise<IaApiPessoaCpf> {
    const digits = cpf.replace(/\D/g, '').slice(0, 11)
    if (digits.length !== 11) {
      throw new Error('Informe um CPF com 11 dígitos.')
    }
    const query = new URLSearchParams({ cpf: digits })
    const payload = await ferramentasFetch<unknown>(`/pessoa-cpf?${query}`, { method: 'GET' })
    const data = unwrapIaApiData(payload)
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const row = data as Record<string, unknown>
      if (row.pessoa && typeof row.pessoa === 'object') {
        return mapPessoa(row.pessoa)
      }
    }
    return mapPessoa(data)
  },
}
